/*
 * Try-on (virtual fitting) parameters — the ONE place to change them (decisions PV-2, PV-3, PV-6 by Javi, 22 Oct 2026).
 * In A1 the gateway mock reads them and enforces them; in Phase B (locked) the backend owns and enforces them and the
 * screens keep reading them through `gateway.getTryOnConfig()`, never from here directly.
 *   requiresSession     only signed-in customers can generate looks (PV-2)
 *   dailyLookQuota      free generated looks per customer and day (PV-3, initial value)
 *   maxGarmentsPerLook  garments a look can hold (PV-6, initial value; Q-007)
 *   consentVersion      version of the try-on consent text the customer accepts (text and design UNKNOWN: not in Figma)
 *   bodySlots           ONE garment per body slot (rule decided with Javi's coordinator, 2026-10-24): slot -> the store collection
 *                       handles that belong to it. Adding a garment whose slot is taken REPLACES the previous one. 'other' (no
 *                       entry here) never replaces. A product's slot is DERIVED from its metadata, never written per product:
 *                       NcTryonConfig.slotOf(record) reads record.collections (handles), then falls back to productType and tags
 *                       matched against the same handles (accents and case ignored, singular or plural), else 'other'.
 * slotOf / SLOT_ORDER / OTHER_SLOT are not enumerable on purpose: getTryOnConfig() copies only the data above.
 */
(function (root) {
  var OTHER_SLOT = 'other';
  var bodySlots = Object.freeze({
    top: Object.freeze(['camisetas', 'polos', 'camisas', 'jerseys', 'sudaderas']),
    outer: Object.freeze(['abrigos']),
    bottom: Object.freeze(['pantalones', 'trajes-de-bano']),
    shoes: Object.freeze(['calzado', 'zapatillas']),
    socks: Object.freeze(['calcetines']),
    head: Object.freeze(['gorras']),
    eyes: Object.freeze(['gafas']),
    belt: Object.freeze(['cinturones']),
    bag: Object.freeze(['carteras'])
  });
  var slotOrder = Object.freeze(Object.keys(bodySlots).concat([OTHER_SLOT]));

  // lower case, no accents, words joined by '-': 'Trajes de baño' -> 'trajes-de-bano'
  function normalize(value) {
    return String(value === undefined || value === null ? '' : value)
      .normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  // singular and plural spellings of a handle, so a product type "Camiseta" or a tag "Pantalón" matches 'camisetas' / 'pantalones'
  function variants(handle) {
    var out = [handle];
    if (/es$/.test(handle)) out.push(handle.slice(0, -2));
    if (/s$/.test(handle)) out.push(handle.slice(0, -1));
    return out;
  }

  var byHandle = {};
  var byVariant = {};
  Object.keys(bodySlots).forEach(function (slot) {
    bodySlots[slot].forEach(function (h) {
      byHandle[h] = slot;
      variants(h).forEach(function (v) { if (!byVariant[v]) byVariant[v] = slot; });
    });
  });

  // slot of a store record { collections: [handles], productType, tags: [] }; also accepts a stored snapshot ({ slot })
  function slotOf(record) {
    if (!record) return OTHER_SLOT;
    var cols = Array.isArray(record.collections) ? record.collections : [];
    for (var i = 0; i < cols.length; i++) {
      var slot = byHandle[normalize(cols[i])];
      if (slot) return slot;
    }
    var fallback = [record.productType].concat(Array.isArray(record.tags) ? record.tags : []);
    for (var j = 0; j < fallback.length; j++) {
      var s = byVariant[normalize(fallback[j])];
      if (s) return s;
    }
    return OTHER_SLOT;
  }

  var config = {
    requiresSession: true,
    dailyLookQuota: 5,
    maxGarmentsPerLook: 5,
    consentVersion: '2026-10-22',
    bodySlots: bodySlots
  };
  Object.defineProperty(config, 'slotOf', { value: slotOf, enumerable: false });
  Object.defineProperty(config, 'normalizeSlotText', { value: normalize, enumerable: false });
  Object.defineProperty(config, 'SLOT_ORDER', { value: slotOrder, enumerable: false });
  Object.defineProperty(config, 'OTHER_SLOT', { value: OTHER_SLOT, enumerable: false });
  config = Object.freeze(config);
  if (typeof module !== 'undefined' && module.exports) module.exports = config;
  else root.NcTryonConfig = config;
})(typeof window !== 'undefined' ? window : this);
